import { FloatingLoader, Loading } from "@/components";
import { useDebounce } from "@/hooks/useDebounce";
import useInvitationMutation from "@/hooks/useInvitationMutation";
import useMessageMutation from "@/hooks/useMessageMutation";
import useProfile from "@/hooks/useProfile";
import usePromptCollection from "@/hooks/usePromptCollection";
import { QUERY_KEYS } from "@/libraries/constant";
import { getMyAlertsQueryOptions, queryClient } from "@/queries";
import { InboxAlertSearchSchema } from "@/schemas";
import type { AlertType } from "@/types";
import { onlineManager, useSuspenseInfiniteQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useVirtualizer } from "@tanstack/react-virtual";
import { zodSearchValidator } from "@tanstack/router-zod-adapter";
import _isEmpty from "lodash/isEmpty";
import {
	Suspense,
	lazy,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import { toast } from "sonner";
import AlertCard from "../-components/AlertCard";
import EmptyInbox from "../-components/EmptyInbox";
const ModalActionInvitation = lazy(
	() => import("../-components/ModalAcceptIvitation"),
);

export const Route = createFileRoute("/dashboard/inbox/alerts/")({
	loader: ({ context, location }) => {
		if (onlineManager.isOnline() && !context.isRestoring) {
			return context.queryClient.ensureInfiniteQueryData(
				getMyAlertsQueryOptions({
					scope: (location.search as any).tab || "my_outstanding",
				}),
			);
		}
		return queryClient.getQueryData([
			QUERY_KEYS.GET_MY_ALERTS,
			{ scope: (location.search as any).tab || "my_outstanding" },
		]);
	},
	validateSearch: zodSearchValidator(InboxAlertSearchSchema),
	component: RouteComponent,
});

function RouteComponent() {
	const scope = Route.useSearch({
		select: (s) => s.tab,
	});
	const searchQ = Route.useSearch({
		select: (s) => s.q || "",
	});
	const searchDataId = Route.useSearch({
		select: (s) => s.id,
	});
	const searchDataType = Route.useSearch({
		select: (s) => s.type,
	});
	const searchAction = Route.useSearch({
		select: (s) => s.action,
	});
	const { invitation } = useInvitationMutation();
	const debounceQ = useDebounce(searchQ, 300);
	const scrollRef = useRef<HTMLDivElement>(null);
	const queryMessages = useSuspenseInfiniteQuery(
		getMyAlertsQueryOptions({
			scope,
		}),
	);
	const { profile } = useProfile();
	const inboxMessages = useMemo(() => {
		if (!debounceQ || !queryMessages.data) return queryMessages.data;
		return queryMessages.data.filter(
			(d) =>
				d.text.toLowerCase().includes(debounceQ) ||
				d.group?.name?.toLowerCase()?.includes(debounceQ) ||
				d.team?.name?.toLowerCase()?.includes(debounceQ) ||
				d.case?.title?.toLowerCase()?.includes(debounceQ),
		);
	}, [queryMessages.data, debounceQ]);
	const isLoading = queryMessages.isLoading || queryMessages.isFetching;
	const isEmpty = useMemo(
		() => !isLoading && !inboxMessages.length && !searchQ,
		[isLoading, searchQ, inboxMessages],
	);
	const isEmptySearch = useMemo(
		() => !isLoading && !inboxMessages.length && !!searchQ,
		[isLoading, searchQ, inboxMessages],
	);

	const [selectedInbox, setSelectedInbox] = useState<{
		type: "accept" | "reject";
		message: AlertType;
	} | null>(null);

	const inboxVirtualizer = useVirtualizer({
		count: inboxMessages.length,
		overscan: 5,
		gap: 12,
		paddingEnd: inboxMessages.length ? 222 : undefined,
		getScrollElement: () => scrollRef.current,
		measureElement: (el) => el.getBoundingClientRect().height,
		estimateSize: () => 50,
		getItemKey: (idx) => inboxMessages[idx].id ?? idx,
	});
	const inboxVirtualizerItems = inboxVirtualizer.getVirtualItems();

	const { acceptMessage, declineMessage } = useMessageMutation();
	const {
		upgradeCase,
		freeCase,
		acceptRequestAccessPrompt,
		declineRequestAccessPrompt,
	} = usePromptCollection({
		currentPlan: profile?.subscription_status,
	});

	const onClickAlert = (msg: NonNullable<typeof selectedInbox>) => {
		if (!_isEmpty(msg.message.case) && !profile?._isProPlan) {
			if (!profile?.case_left) return upgradeCase(profile?._maxCase);
			return freeCase(profile?.case_left, () => {
				if (msg.message.tags !== "request") return setSelectedInbox(msg);
				return msg.type === "accept"
					? acceptRequestAccessPrompt(() => acceptMessage.mutate(msg.message))
					: declineRequestAccessPrompt(() =>
							declineMessage.mutate(msg.message),
						);
			});
		}
		if (msg.message.tags !== "request") return setSelectedInbox(msg);
		return msg.type === "accept"
			? acceptRequestAccessPrompt(() => acceptMessage.mutate(msg.message))
			: declineRequestAccessPrompt(() => declineMessage.mutate(msg.message));
	};

	const onClickAccept = (message: AlertType) => {
		const invitationType = message?.channel_name
			? message.channel_name.includes("case") ||
				message.channel_name.includes("external")
				? ("Case" as const)
				: ("Group" as const)
			: ("Team" as const);
		const data =
			invitationType === "Case" && !_isEmpty(message.case)
				? message.case
				: invitationType === "Group" && !_isEmpty(message.group)
					? message.group
					: invitationType === "Team" && !_isEmpty(message.team)
						? message.team
						: undefined;
		if (!data || !invitationType) return;
		return invitation.mutate(
			{
				inviteable_type: invitationType,
				inviteable_id: data.id,
				message_id: message.id_db,
				status: "accept",
			},
			{
				onSuccess: () => {
					setSelectedInbox(null);
					toast.success("Alert completed and moved to history");
				},
			},
		);
	};
	const onClickReject = (message: AlertType) => {
		const invitationType = message?.channel_name
			? message.channel_name.includes("case") ||
				message.channel_name.includes("external")
				? ("Case" as const)
				: ("Group" as const)
			: ("Team" as const);
		const data =
			invitationType === "Case" && !_isEmpty(message.case)
				? message.case
				: invitationType === "Group" && !_isEmpty(message.group)
					? message.group
					: invitationType === "Team" && !_isEmpty(message.team)
						? message.team
						: undefined;
		if (!data || !invitationType) return;
		return invitation.mutate(
			{
				inviteable_type: invitationType,
				inviteable_id: data.id,
				message_id: message.id_db,
				status: "decline",
			},
			{
				onSuccess: () => {
					setSelectedInbox(null);
					toast.success("Alert completed and moved to history");
				},
			},
		);
	};

	const handleOnRetieveAction = useCallback(() => {
		const selectedMessage = inboxMessages.find((m) => {
			switch (searchDataType) {
				case "Team":
					return m.team?.id === searchDataId;
				case "Group":
					return m.group?.id === searchDataId;
				case "Case":
					return m.case?.id === searchDataId;
				default:
					return false;
			}
		});
		if (!selectedMessage) return;
		return setSelectedInbox({
			message: selectedMessage,
			type: searchAction,
		});
	}, [inboxMessages, searchDataId, searchDataType, searchAction]);

	useEffect(() => {
		if (searchDataId && searchDataType && searchAction) {
			handleOnRetieveAction();
		}
	}, [searchDataId, searchDataType, searchAction, handleOnRetieveAction]);

	useEffect(() => {
		const [lastItem] = [...inboxVirtualizerItems].reverse();

		if (!lastItem) return;

		if (
			lastItem.index >= inboxMessages.length - 1 &&
			queryMessages.hasNextPage &&
			!queryMessages.isFetchingNextPage
		) {
			queryMessages.fetchNextPage();
		}
	}, [
		queryMessages.hasNextPage,
		queryMessages.fetchNextPage,
		inboxMessages.length,
		queryMessages.isFetchingNextPage,
		inboxVirtualizerItems,
	]);

	return (
		<div
			ref={scrollRef}
			className="relative h-full w-full overflow-y-auto pb-4"
		>
			<FloatingLoader open={isLoading || queryMessages.isFetchingNextPage} />
			{!isEmpty || !isEmptySearch ? (
				<div
					className="relative w-full"
					style={{
						height: inboxVirtualizer.getTotalSize(),
					}}
				>
					{inboxVirtualizerItems.map((inboxVirtualizerItem) => {
						const inboxMessage =
							inboxMessages.find((m) => m.id === inboxVirtualizerItem.key) ??
							inboxMessages.at(inboxVirtualizerItem.index);
						if (!inboxMessage) return;
						return (
							<div
								key={inboxVirtualizerItem.key}
								data-index={inboxVirtualizerItem.index}
								ref={inboxVirtualizer.measureElement}
								className="container w-full absolute top-0 left-0"
								style={{
									transform: `translateY(${inboxVirtualizerItem.start}px)`,
								}}
							>
								<AlertCard
									type={scope}
									message={inboxMessage}
									onAcceptInvitation={() =>
										onClickAlert({
											type: "accept",
											message: inboxMessage,
										})
									}
									onDeclineInvitation={() =>
										onClickAlert({
											type: "reject",
											message: inboxMessage,
										})
									}
								/>
							</div>
						);
					})}
				</div>
			) : null}
			{isEmpty ? <EmptyInbox /> : null}
			{isEmptySearch ? (
				<EmptyInbox title={`No results found for '${debounceQ}'`} />
			) : null}
			<Suspense>
				{selectedInbox && selectedInbox.message.tags !== "request" ? (
					<ModalActionInvitation
						isOpen={!!selectedInbox}
						message={selectedInbox?.message}
						onClose={() => setSelectedInbox(null)}
						onAccept={
							selectedInbox?.type === "accept" ? onClickAccept : undefined
						}
						onReject={
							selectedInbox?.type === "reject" ? onClickReject : undefined
						}
					/>
				) : null}
			</Suspense>
			<Loading
				status={
					invitation.isPending ||
					acceptMessage.isPending ||
					declineMessage.isPending
				}
			/>
		</div>
	);
}
